import { useEffect, useState } from "react";
import { Badge } from "./Badge";
import { User, Users, Shield, UserX, Filter } from "lucide-react";
import useApiHook from "../../../../utils/useApi";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store";
import FilterRoles from "./filters/FilterRoles";
import {
  TeamsTableApiResponse,
  TeamsUserData,
} from "../../../../interface/types";

const TeamsTable = () => {
  const [selectedRoleRef, setSelectedRoleRef] = useState<string | null>(null);
  const [displayData, setDisplayData] = useState<TeamsUserData[]>([]);

  const merchant = useSelector((state: RootState) => state?.merchant?.profile);

  const {
    data: mainData,
    isLoading: mainLoading,
    makeRequest: mainReq,
  } = useApiHook<TeamsTableApiResponse>("/dashboard/users", {
    method: "get",
  });

  const {
    data: subData,
    isLoading: subLoading,
    makeRequest: subReq,
  } = useApiHook<TeamsTableApiResponse>(
    selectedRoleRef ? `/admin/role-access/${selectedRoleRef}` : "",
    {
      method: "get",
    }
  );

  useEffect(() => {
    mainReq();
  }, []);

  useEffect(() => {
    if (selectedRoleRef) {
      subReq();
    }
  }, [selectedRoleRef]);

  useEffect(() => {
    if (selectedRoleRef && subData?.data) {
      setDisplayData(subData.data);
    } else if (mainData?.data) {
      setDisplayData(mainData.data);
    } else {
      setDisplayData([]);
    }
  }, [selectedRoleRef, mainData, subData]);

  if (mainLoading || (selectedRoleRef && subLoading)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const teamMembersCount = displayData.length;
  const teamMembersText =
    teamMembersCount === 1 ? "Team Member" : "Team Members";

  return (
    <div>
      {/* filters */}
      <div className="flex items-center mb-4 px-2">
        <div className="flex items-center py-2 rounded-md">
          <span className="text-gray-500 text-sm font-medium">
            {teamMembersText} - {teamMembersCount}
          </span>
        </div>
        <div className="mx-2 h-[1px] w-4 bg-gray-300"></div>
        <FilterRoles setSelectedRoleRef={setSelectedRoleRef} />
      </div>

      {/* end of filters */}

      {displayData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg">
          <UserX className="h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">
            No team members found
          </h3>
          <p className="text-sm text-gray-500 mt-2">
            {selectedRoleRef
              ? "No team members found for the selected role."
              : "No team members have been created yet."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Name
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    Email Address
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    Role
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayData.map((user, index) => {
                const isCurrentUser = user.username === merchant?.username;
                const fullName = `${user.userFirstName} ${user.userLastName}`;

                return (
                  <tr key={index} className={isCurrentUser ? "bg-blue-50" : ""}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-xs font-medium text-gray-900 flex items-center">
                            {fullName}
                            {isCurrentUser && (
                              <Badge className="ml-2">You</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500">
                        {user.username}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {user.roleName}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-xs text-gray-400">
                      No actions available
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TeamsTable;
